<template>
  <div
    class="w-full bg-stone-50 dark:bg-stone-950 font-sans overflow-hidden agent-create-shell"
    :style="wizardShellStyle"
  >
    <div class="px-4 pt-4 pb-2 shrink-0" dir="ltr">
      <button
        type="button"
        @click="restartWizard"
        :class="['flex items-center p-2 px-3 text-stone-700 dark:text-stone-100 bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700 rounded-full transition-colors font-medium text-sm border border-stone-200 dark:border-stone-700', isRTL === true ? 'flex-row-reverse' : 'flex-row']"
        :title="t('agentCreate.restart', 'Start over')"
        :dir="isRTL === true ? 'rtl' : 'ltr'"
      >
        <Icon icon="tabler:rotate" width="18" height="18" />
        <span :class="isRTL === true ? 'mr-1.5' : 'ml-1.5'">{{ t('agentCreate.restart', 'Start over') }}</span>
      </button>
    </div>

    <div class="px-4 pb-2 shrink-0">
      <div class="max-w-3xl mx-auto w-full rounded-2xl border border-stone-200 bg-white px-5 py-3.5 text-sm text-stone-700 dark:border-stone-700 dark:bg-stone-900 dark:text-stone-200" :dir="isRTL === true ? 'rtl' : 'ltr'">
        <div :class="['flex w-full items-start', isRTL === true ? 'flex-row-reverse text-right' : 'flex-row text-left']">
          <Icon icon="tabler:message-circle-2" width="18" height="18" class="mt-0.5 flex-shrink-0 text-stone-500 dark:text-stone-400" />
          <span :class="['flex-1 leading-6', isRTL === true ? 'mr-3' : 'ml-3']">{{ t('agentCreate.banner', 'Answer a few questions and we will generate your AI agent for you.') }}</span>
        </div>
      </div>
    </div>

    <div ref="scrollContainerRef" class="agent-create-scroll overflow-y-auto px-4 py-4 min-h-0">
      <div class="w-full max-w-3xl mx-auto flex flex-col items-center min-h-full justify-center py-2">
            <div v-if="currentStep !== 'generating' && currentStep !== 'preview' && currentStep !== 'knowledgeProcessing'" class="w-16 h-16 rounded-2xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 flex items-center justify-center shadow-sm mb-5 shrink-0">
              <Icon icon="tabler:clipboard-text" width="30" class="text-stone-600 dark:text-stone-300" />
            </div>

            <StreamingQuestion
              v-if="currentStep !== 'generating' && currentStep !== 'knowledgeProcessing'"
              :key="questionKey"
              :text="currentQuestion"
              @complete="onQuestionStreamComplete"
            />

            <transition name="step-fade">
              <div v-if="showStepContent === true && currentStep !== 'preview' && currentStep !== 'generating' && currentStep !== 'knowledgeProcessing'" class="w-full flex justify-center mt-2">
                <AgentCreateTypeStep
                  v-if="currentStep === 'type'"
                  ref="typeStepRef"
                  :selected-type="selectedTypeId"
                  @update:selected-type="onTypeSelected"
                />
                <AgentCreateJobStep
                  v-else-if="currentStep === 'job'"
                  ref="jobStepRef"
                  :selected-job="selectedJobId"
                  @update:selected-job="onJobSelected"
                />
                <AgentCreatePersonalityStep
                  v-else-if="currentStep === 'personality'"
                  ref="personalityStepRef"
                />
                <AgentCreateKnowledgeStep
                  v-else-if="currentStep === 'knowledge'"
                  ref="knowledgeStepRef"
                  @submit="onKnowledgeSubmit"
                  @select="onKnowledgeSelected"
                  @advance-sources="onKnowledgeAdvanceSources"
                  @sources-summary="onKnowledgeSourcesSummary"
                />
                <AgentCreateQuestionAnswerStep
                  v-else-if="currentStep === 'questionAnswers'"
                  ref="questionAnswerStepRef"
                  @update:entries="onQuestionAnswersUpdate"
                />
                <AgentCreateOrganizationStep
                  v-else-if="currentStep === 'organization'"
                  ref="organizationStepRef"
                  @update:organization="onOrganizationUpdate"
                />
                <AgentCreateIntegrationsStep
                  v-else-if="currentStep === 'integrations'"
                  ref="integrationsStepRef"
                  @update:integrations="onIntegrationsUpdate"
                />
                <AgentCreatePlatformsStep
                  v-else-if="currentStep === 'platforms'"
                  ref="platformsStepRef"
                  @submit="onPlatformsSubmit"
                  @selection-change="onPlatformsSelectionChange"
                />
              </div>
            </transition>

            <transition name="step-fade">
              <div v-if="currentStep === 'knowledgeProcessing'" class="w-full flex flex-col items-center mt-2">
                <div class="w-full max-w-2xl text-center mb-5" :dir="isRTL === true ? 'rtl' : 'ltr'">
                  <h2 class="text-lg font-semibold text-slate-800 dark:text-slate-100">{{ t('agentCreate.knowledge.processing.title', 'Scanning knowledge sources') }}</h2>
                  <p class="text-sm text-slate-600 dark:text-slate-400 mt-1">{{ t('agentCreate.knowledge.processing.subtitle', 'We are indexing each source. This may take a moment.') }}</p>
                </div>
                <AgentCreateKnowledgeProcessing :sources="pendingKnowledgeSources" @complete="onKnowledgeProcessingComplete" />
              </div>
            </transition>

            <transition name="step-fade">
              <div v-if="currentStep === 'generating'" class="w-full flex justify-center mt-2">
                <AgentCreateGenerating :create-agent="createBaselineAgent" @complete="onGeneratingComplete" @error="onAgentCreationError" />
              </div>
            </transition>

            <transition name="step-fade">
              <div v-if="currentStep === 'preview' && showStepContent === true" class="w-full flex justify-center">
                <AgentCreatePreview :preview="previewData" />
              </div>
            </transition>
      </div>
    </div>

    <div v-if="currentStep !== 'generating' && currentStep !== 'preview' && currentStep !== 'knowledgeProcessing'" class="agent-create-footer px-4 py-2 bg-white/90 dark:bg-stone-950/90 border-t border-stone-200 dark:border-stone-800 z-20 backdrop-blur">
      <div class="max-w-3xl mx-auto w-full">
        <div
          class="rounded-[26px] p-3 shadow-sm relative transition-all border"
          :class="isInputLocked === true ? 'bg-gray-100 dark:bg-gray-800/90 border-gray-200 dark:border-gray-700' : 'bg-white dark:bg-stone-900 border-stone-200 dark:border-stone-700 focus-within:ring-1 focus-within:ring-stone-300 dark:focus-within:ring-stone-600'"
        >
          <textarea
            v-model="userInput"
            :style="{ direction: inputDirection, textAlign: inputDirection === 'rtl' ? 'right' : 'left' }"
            :placeholder="inputPlaceholder"
            :disabled="isInputLocked === true"
            class="w-full border-none p-2 focus:ring-0 resize-none max-h-[200px] overflow-y-auto leading-relaxed min-h-[44px] disabled:cursor-not-allowed"
            :class="isInputLocked === true ? 'bg-transparent text-gray-500 dark:text-gray-400 placeholder-gray-400 dark:placeholder-gray-500' : 'bg-transparent text-gray-800 dark:text-gray-100 placeholder-gray-500'"
            @keydown.enter.exact.prevent="sendMessage"
            @input="checkDirection($event.target.value)"
            ref="inputRef"
            rows="1"
          ></textarea>
          <div class="flex justify-end items-center mt-2 px-1">
            <button
              type="button"
              class="p-2 rounded-full bg-stone-700 dark:bg-stone-200 text-white dark:text-stone-900 disabled:opacity-20 disabled:cursor-not-allowed transition-all hover:bg-stone-800 dark:hover:bg-white"
              :disabled="canSend === false || isSending === true"
              @click="sendMessage"
            >
              <Icon v-if="isSending === true" icon="eos-icons:loading" width="20" height="20" />
              <Icon v-else icon="tabler:arrow-up" width="20" height="20" />
            </button>
          </div>
        </div>
        <div class="text-center mt-2">
          <span class="text-xs text-gray-400 dark:text-gray-500">{{ t('agentCreate.disclaimer', 'You can refine everything later in the full agent editor.') }}</span>
        </div>
      </div>
    </div>
    <dialog :open="creationError !== ''" class="modal">
      <div class="modal-box">
        <h3 class="font-bold text-lg">{{ t('agentCreate.creationError.title', 'Could not create the agent') }}</h3>
        <p class="py-4">{{ creationError }}</p>
        <div class="modal-action">
          <button type="button" class="btn" @click="closeCreationError">{{ t('common.close', 'Close') }}</button>
        </div>
      </div>
    </dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, inject, nextTick, watch } from 'vue';
import { Icon } from '@iconify/vue/dist/offline';
import axios from '@/api/axiosInstance';
import { globalState } from '@/globalState';
import { useI18n } from '@/i18n';
import StreamingQuestion from '@/components/agentCreate/StreamingQuestion.vue';
import AgentCreateTypeStep from '@/components/agentCreate/AgentCreateTypeStep.vue';
import AgentCreateJobStep from '@/components/agentCreate/AgentCreateJobStep.vue';
import AgentCreatePersonalityStep from '@/components/agentCreate/AgentCreatePersonalityStep.vue';
import AgentCreateKnowledgeStep from '@/components/agentCreate/AgentCreateKnowledgeStep.vue';
import AgentCreateQuestionAnswerStep from '@/components/agentCreate/AgentCreateQuestionAnswerStep.vue';
import AgentCreateOrganizationStep from '@/components/agentCreate/AgentCreateOrganizationStep.vue';
import AgentCreateIntegrationsStep from '@/components/agentCreate/AgentCreateIntegrationsStep.vue';
import AgentCreateKnowledgeProcessing from '@/components/agentCreate/AgentCreateKnowledgeProcessing.vue';
import AgentCreatePlatformsStep from '@/components/agentCreate/AgentCreatePlatformsStep.vue';
import AgentCreateGenerating from '@/components/agentCreate/AgentCreateGenerating.vue';
import AgentCreatePreview from '@/components/agentCreate/AgentCreatePreview.vue';

const hideSidebar = inject('hideSidebar');
const { t, currentLanguage, availableLanguages } = useI18n();

const wizardShellStyle = {
  height: 'calc(100vh - 64px)',
  maxHeight: 'calc(100vh - 64px)',
  display: 'grid',
  gridTemplateRows: 'auto auto minmax(0, 1fr) auto'
};

const inputRef = ref(null);
const typeStepRef = ref(null);
const jobStepRef = ref(null);
const personalityStepRef = ref(null);
const knowledgeStepRef = ref(null);
const questionAnswerStepRef = ref(null);
const organizationStepRef = ref(null);
const integrationsStepRef = ref(null);
const platformsStepRef = ref(null);
const scrollContainerRef = ref(null);
const pendingKnowledgeSources = ref([]);
const knowledgeSubStep = ref('');
const userInput = ref('');
const inputDirection = ref('ltr');
const isSending = ref(false);
const selectedTypeId = ref('');
const selectedJobId = ref('');
const creationError = ref('');

const isRTL = computed(() => {
  const language = availableLanguages.value.find((item) => item.code === currentLanguage.value);
  if (language === undefined || language === null) {
    return false;
  }
  return language.dir === 'rtl';
});

const currentStep = ref('type');
const showStepContent = ref(false);
const questionKey = ref(0);
const historyEntries = ref([]);
let historyCounter = 0;

const wizardData = ref({
  typeLabel: '',
  typeId: '',
  customType: '',
  jobDescription: '',
  agentName: '',
  personality: '',
  gender: '',
  knowledgeType: '',
  knowledgeLabel: '',
  knowledgeSources: [],
  questionAnswers: [],
  organizationName: '',
  organizationDescription: '',
  selectedToolIds: [],
  toolSettings: {},
  selectedTools: [],
  channels: [],
  whatsappSourceId: null,
  whatsappNumber: '',
  phoneSourceId: null,
  phoneNumber: '',
  agentId: ''
});

const stepQuestions = computed(() => ({
  type: t('agentCreate.questions.type', "Let's start — what type of agent would you like to create?"),
  job: t('agentCreate.questions.job', 'What job will this agent represent?'),
  personality: t('agentCreate.questions.personality', 'Describe the name, personality, and gender.'),
  knowledge: t('agentCreate.questions.knowledge', "Let's work on knowledge! Choose the creation type:"),
  knowledgeSources: t('agentCreate.questions.knowledgeSources', 'Great! How would you like to add knowledge sources?'),
  questionAnswers: t('agentCreate.questions.questionAnswers', 'Add the questions and answers your agent should know.'),
  organization: t('agentCreate.questions.organization', 'Tell us about the organization this agent represents.'),
  integrations: t('agentCreate.questions.integrations', 'Would you like to connect any integrations?'),
  platforms: t('agentCreate.questions.platforms', 'On which platforms would you like to set it up?'),
  preview: t('agentCreate.questions.preview', 'Your agent is ready!')
}));

const currentQuestion = computed(() => {
  if (currentStep.value === 'knowledge' && knowledgeSubStep.value === 'sources') {
    return stepQuestions.value.knowledgeSources;
  }
  const question = stepQuestions.value[currentStep.value];
  if (typeof question === 'string') {
    return question;
  }
  return '';
});

const previewData = computed(() => {
  return {
    agentId: wizardData.value.agentId,
    agentName: wizardData.value.agentName,
    typeLabel: wizardData.value.typeLabel,
    jobDescription: wizardData.value.jobDescription,
    knowledgeLabel: wizardData.value.knowledgeLabel,
    channels: wizardData.value.channels,
    phoneNumber: wizardData.value.phoneNumber,
    whatsappNumber: wizardData.value.whatsappNumber
  };
});

const isInputLocked = computed(() => {
  if (currentStep.value === 'personality') {
    return true;
  }
  if (currentStep.value === 'knowledge') {
    return true;
  }
  if (currentStep.value === 'questionAnswers') {
    return true;
  }
  if (currentStep.value === 'organization') {
    return true;
  }
  if (currentStep.value === 'integrations') {
    return true;
  }
  if (currentStep.value === 'platforms') {
    return true;
  }
  return false;
});

const inputPlaceholder = computed(() => {
  if (currentStep.value === 'type') {
    return t('agentCreate.type.customPlaceholder', 'Or describe your own agent type...');
  }
  if (currentStep.value === 'job') {
    return t('agentCreate.job.placeholder', 'e.g. Front-line website support agent that answers product questions...');
  }
  if (currentStep.value === 'personality') {
    return t('agentCreate.personality.lockedInputHint', 'Your selected role is shown here');
  }
  if (currentStep.value === 'knowledge') {
    if (knowledgeSubStep.value === 'sources') {
      return t('agentCreate.knowledge.sources.inputHint', 'Add at least one knowledge source above');
    }
    return t('agentCreate.knowledge.inputHint', 'Select a knowledge type above');
  }
  if (currentStep.value === 'platforms') {
    return t('agentCreate.platforms.inputHint', 'Select platforms above');
  }
  return t('chat.messagePlaceholder', 'Message');
});

const canSend = computed(() => {
  const text = typeof userInput.value === 'string' ? userInput.value.trim() : '';
  if (currentStep.value === 'type') {
    if (selectedTypeId.value !== '') {
      return true;
    }
    return text.length > 0;
  }
  if (currentStep.value === 'job') {
    if (selectedJobId.value !== '') {
      return true;
    }
    return text.length >= 8;
  }
  if (currentStep.value === 'personality') {
    if (personalityStepRef.value === null || personalityStepRef.value === undefined) {
      return false;
    }
    return personalityStepRef.value.canSubmit() === true;
  }
  if (currentStep.value === 'knowledge') {
    if (knowledgeStepRef.value === null || knowledgeStepRef.value === undefined) {
      return false;
    }
    return knowledgeStepRef.value.isReady() === true;
  }
  if (currentStep.value === 'platforms') {
    if (platformsStepRef.value === null || platformsStepRef.value === undefined) {
      return false;
    }
    return platformsStepRef.value.isReady() === true;
  }
  return false;
});

watch(userInput, () => {
  if (currentStep.value === 'type') {
    if (typeof userInput.value === 'string' && userInput.value.trim().length > 0) {
      if (selectedTypeId.value !== '') {
        const presetLabel = resolveTypeLabelFromSelection(selectedTypeId.value);
        if (userInput.value.trim() !== presetLabel) {
          selectedTypeId.value = '';
        }
      }
    }
  }
  if (currentStep.value === 'job') {
    if (typeof userInput.value === 'string' && userInput.value.trim().length > 0) {
      if (selectedJobId.value !== '') {
        const presetLabel = resolveJobLabelFromSelection(selectedJobId.value);
        if (userInput.value.trim() !== presetLabel) {
          selectedJobId.value = '';
        }
      }
    }
  }
  nextTick(() => {
    if (inputRef.value !== null && inputRef.value !== undefined) {
      inputRef.value.style.height = 'auto';
      inputRef.value.style.height = Math.min(inputRef.value.scrollHeight, 200) + 'px';
    }
  });
});

watch(currentStep, (step) => {
  if (step !== 'knowledge') {
    knowledgeSubStep.value = '';
  }
  if (step === 'personality' && typeof wizardData.value.jobDescription === 'string' && wizardData.value.jobDescription.length > 0) {
    userInput.value = wizardData.value.jobDescription;
    checkDirection(userInput.value);
  } else {
    userInput.value = '';
    inputDirection.value = isRTL.value === true ? 'rtl' : 'ltr';
  }
  nextTick(() => {
    if (inputRef.value !== null && inputRef.value !== undefined) {
      inputRef.value.style.height = 'auto';
      inputRef.value.style.height = '44px';
    }
  });
});

function checkDirection(text) {
  if (text === undefined || text === null || text === '') {
    inputDirection.value = isRTL.value === true ? 'rtl' : 'ltr';
    return;
  }
  const rtlRegex = /[\u0590-\u05FF\u0600-\u06FF]/;
  inputDirection.value = rtlRegex.test(text) === true ? 'rtl' : 'ltr';
}

function onTypeSelected(typeId) {
  selectedTypeId.value = typeId;
  userInput.value = resolveTypeLabelFromSelection(typeId);
  checkDirection(userInput.value);
  resizeInput();
}

function onJobSelected(jobId) {
  selectedJobId.value = jobId;
  userInput.value = resolveJobLabelFromSelection(jobId);
  checkDirection(userInput.value);
  resizeInput();
}

function onKnowledgeSelected(payload) {
  if (payload !== null && payload !== undefined && typeof payload.label === 'string') {
    userInput.value = payload.label;
    checkDirection(userInput.value);
    resizeInput();
  }
}

function onPlatformsSelectionChange(summary) {
  if (typeof summary === 'string') {
    userInput.value = summary;
    checkDirection(userInput.value);
    resizeInput();
  }
}

function resolveTypeLabelFromSelection(typeId) {
  if (typeStepRef.value !== null && typeStepRef.value !== undefined && typeof typeStepRef.value.resolveLabel === 'function') {
    return typeStepRef.value.resolveLabel(typeId, '');
  }
  return '';
}

function resolveJobLabelFromSelection(jobId) {
  if (jobStepRef.value !== null && jobStepRef.value !== undefined && typeof jobStepRef.value.resolveLabel === 'function') {
    return jobStepRef.value.resolveLabel(jobId, '');
  }
  return '';
}

function resizeInput() {
  nextTick(() => {
    if (inputRef.value !== null && inputRef.value !== undefined) {
      inputRef.value.style.height = 'auto';
      inputRef.value.style.height = Math.min(inputRef.value.scrollHeight, 200) + 'px';
    }
  });
}

function resolveTypeLabel() {
  const customType = typeof userInput.value === 'string' ? userInput.value.trim() : '';
  if (typeStepRef.value !== null && typeStepRef.value !== undefined && typeof typeStepRef.value.resolveLabel === 'function') {
    return typeStepRef.value.resolveLabel(selectedTypeId.value, customType);
  }
  return customType;
}

function resolveJobLabel() {
  const customJob = typeof userInput.value === 'string' ? userInput.value.trim() : '';
  if (jobStepRef.value !== null && jobStepRef.value !== undefined && typeof jobStepRef.value.resolveLabel === 'function') {
    return jobStepRef.value.resolveLabel(selectedJobId.value, customJob);
  }
  return customJob;
}

function sendMessage() {
  if (canSend.value === false || isSending.value === true) {
    return;
  }
  isSending.value = true;
  const text = typeof userInput.value === 'string' ? userInput.value.trim() : '';
  if (currentStep.value === 'type') {
    onTypeSubmit({
      typeId: selectedTypeId.value,
      customType: text,
      label: resolveTypeLabel()
    });
    userInput.value = '';
    selectedTypeId.value = '';
  } else if (currentStep.value === 'job') {
    onJobSubmit({ jobDescription: resolveJobLabel() });
    userInput.value = '';
    selectedJobId.value = '';
  } else if (currentStep.value === 'personality') {
    if (personalityStepRef.value !== null && personalityStepRef.value !== undefined) {
      const payload = personalityStepRef.value.buildPayload();
      onPersonalitySubmit(payload);
      userInput.value = '';
    }
  } else if (currentStep.value === 'knowledge') {
    if (knowledgeStepRef.value !== null && knowledgeStepRef.value !== undefined) {
      knowledgeStepRef.value.submit();
    }
  } else if (currentStep.value === 'questionAnswers') {
    onQuestionAnswersSubmit();
  } else if (currentStep.value === 'organization') {
    if (organizationStepRef.value !== null && organizationStepRef.value !== undefined) {
      onOrganizationSubmit(organizationStepRef.value.buildPayload());
    }
  } else if (currentStep.value === 'integrations') {
    onIntegrationsSubmit();
  } else if (currentStep.value === 'platforms') {
    if (platformsStepRef.value !== null && platformsStepRef.value !== undefined) {
      platformsStepRef.value.submit();
    }
  }
  window.setTimeout(() => {
    isSending.value = false;
  }, 300);
}

function pushHistory(stepKey, question, answer) {
  historyCounter = historyCounter + 1;
  historyEntries.value = historyEntries.value.concat([{
    id: `history-${historyCounter}`,
    stepKey,
    question,
    answer
  }]);
}

function onKnowledgeSourcesSummary(summary) {
  if (typeof summary === 'string' && summary.length > 0) {
    userInput.value = summary;
    checkDirection(userInput.value);
    resizeInput();
  }
}

function onKnowledgeAdvanceSources(payload) {
  if (payload !== null && payload !== undefined && typeof payload.label === 'string') {
    userInput.value = payload.label;
    checkDirection(userInput.value);
    resizeInput();
  }
  knowledgeSubStep.value = 'sources';
  showStepContent.value = false;
  questionKey.value = questionKey.value + 1;
  scrollWizardToBottom();
}

function scrollWizardToBottom() {
  nextTick(() => {
    if (scrollContainerRef.value !== null && scrollContainerRef.value !== undefined) {
      scrollContainerRef.value.scrollTop = scrollContainerRef.value.scrollHeight;
    }
  });
}

function scrollToBottom() {
  scrollWizardToBottom();
}

function onQuestionStreamComplete() {
  showStepContent.value = true;
  if (currentStep.value === 'knowledge' && knowledgeSubStep.value === 'sources') {
    scrollWizardToBottom();
    window.setTimeout(() => {
      scrollWizardToBottom();
    }, 120);
  }
}

function goToStep(step) {
  showStepContent.value = false;
  currentStep.value = step;
  questionKey.value = questionKey.value + 1;
  scrollToBottom();
}

function onTypeSubmit(payload) {
  wizardData.value.typeId = payload.typeId;
  wizardData.value.customType = payload.customType;
  wizardData.value.typeLabel = payload.label;
  pushHistory('type', stepQuestions.value.type, payload.label);
  goToStep('job');
}

function onJobSubmit(payload) {
  wizardData.value.jobDescription = payload.jobDescription;
  pushHistory('job', stepQuestions.value.job, payload.jobDescription);
  goToStep('personality');
}

function onPersonalitySubmit(payload) {
  wizardData.value.agentName = payload.agentName;
  wizardData.value.personality = payload.personality;
  wizardData.value.gender = payload.gender;
  const summary = `${payload.agentName} — ${payload.personality} (${payload.gender})`;
  pushHistory('personality', stepQuestions.value.personality, summary);
  goToStep('knowledge');
}

function needsKnowledgeProcessing(knowledgeType, sources) {
  if (knowledgeType === 'unstructured' || knowledgeType === 'all') {
    if (Array.isArray(sources) === true && sources.length > 0) {
      return true;
    }
  }
  return false;
}

function buildKnowledgeHistoryAnswer(label, sources) {
  if (Array.isArray(sources) === false || sources.length === 0) {
    return label;
  }
  const countLabel = t('agentCreate.knowledge.sources.historyCount', '{count} sources', { count: sources.length });
  return `${label} — ${countLabel}`;
}

function onKnowledgeSubmit(payload) {
  wizardData.value.knowledgeType = payload.knowledgeType;
  wizardData.value.knowledgeLabel = payload.label;
  const sources = Array.isArray(payload.sources) === true ? payload.sources : [];
  wizardData.value.knowledgeSources = sources;
  wizardData.value.questionAnswers = Array.isArray(payload.questionAnswers) === true ? payload.questionAnswers : [];
  if (payload.knowledgeType === 'qa') {
    pushHistory('knowledge', stepQuestions.value.knowledge, payload.label);
    goToStep('questionAnswers');
    return;
  }
  if (needsKnowledgeProcessing(payload.knowledgeType, sources) === true) {
    pendingKnowledgeSources.value = sources;
    showStepContent.value = true;
    currentStep.value = 'knowledgeProcessing';
    questionKey.value = questionKey.value + 1;
    scrollToBottom();
    return;
  }
  pushHistory('knowledge', stepQuestions.value.knowledge, buildKnowledgeHistoryAnswer(payload.label, sources));
  goToStep('organization');
}

function onQuestionAnswersUpdate(entries) {
  wizardData.value.questionAnswers = Array.isArray(entries) === true ? entries : [];
}

function onQuestionAnswersSubmit() {
  const entries = wizardData.value.questionAnswers;
  const countLabel = t('agentCreate.questionAnswers.historyCount', '{count} questions and answers', { count: entries.length });
  pushHistory('questionAnswers', stepQuestions.value.questionAnswers, countLabel);
  goToStep('organization');
}

function onOrganizationUpdate(payload) {
  wizardData.value.organizationName = payload !== null && payload !== undefined && typeof payload.name === 'string' ? payload.name : '';
  wizardData.value.organizationDescription = payload !== null && payload !== undefined && typeof payload.description === 'string' ? payload.description : '';
}

function onOrganizationSubmit(payload) {
  const organizationName = payload !== null && payload !== undefined && typeof payload.name === 'string' ? payload.name.trim() : '';
  const organizationDescription = payload !== null && payload !== undefined && typeof payload.description === 'string' ? payload.description.trim() : '';
  if (organizationName.length === 0 || organizationDescription.length < 8) {
    return;
  }
  wizardData.value.organizationName = organizationName;
  wizardData.value.organizationDescription = organizationDescription;
  pushHistory('organization', stepQuestions.value.organization, organizationName);
  goToStep('integrations');
}

function onIntegrationsUpdate(payload) {
  wizardData.value.selectedToolIds = payload !== null && payload !== undefined && Array.isArray(payload.selectedToolIds) === true ? payload.selectedToolIds : [];
  wizardData.value.toolSettings = payload !== null && payload !== undefined && payload.toolSettings !== null && typeof payload.toolSettings === 'object' ? payload.toolSettings : {};
  wizardData.value.selectedTools = payload !== null && payload !== undefined && Array.isArray(payload.selectedTools) === true ? payload.selectedTools : [];
}

function onIntegrationsSubmit() {
  const countLabel = t('agentCreate.integrations.historyCount', '{count} integrations selected', { count: wizardData.value.selectedToolIds.length });
  pushHistory('integrations', stepQuestions.value.integrations, countLabel);
  goToStep('platforms');
}

function onKnowledgeProcessingComplete() {
  const label = wizardData.value.knowledgeLabel;
  const sources = wizardData.value.knowledgeSources;
  pushHistory('knowledge', stepQuestions.value.knowledge, buildKnowledgeHistoryAnswer(label, sources));
  pendingKnowledgeSources.value = [];
  goToStep('organization');
}

function onPlatformsSubmit(payload) {
  wizardData.value.channels = payload.channels;
  wizardData.value.whatsappSourceId = payload.whatsappSourceId;
  wizardData.value.whatsappNumber = payload.whatsappNumber;
  wizardData.value.phoneSourceId = payload.phoneSourceId;
  wizardData.value.phoneNumber = payload.phoneNumber;
  const channelSummary = payload.channels.join(', ');
  pushHistory('platforms', stepQuestions.value.platforms, channelSummary);
  showStepContent.value = false;
  currentStep.value = 'generating';
  questionKey.value = questionKey.value + 1;
  scrollToBottom();
}

function buildQuestionAnswerFiles() {
  const entries = Array.isArray(wizardData.value.questionAnswers) === true ? wizardData.value.questionAnswers : [];
  const files = [];
  let index = 0;
  while (index < entries.length) {
    const entry = entries[index];
    const question = entry !== undefined && entry !== null && typeof entry.question === 'string' ? entry.question.trim() : '';
    const answer = entry !== undefined && entry !== null && typeof entry.answer === 'string' ? entry.answer.trim() : '';
    if (question.length > 0 && answer.length > 0) {
      files.push({
        fileName: `question-answer-${index + 1}.txt`,
        fileType: 'questionAnswer',
        status: 'new',
        content: `Question: ${question}\nAnswer: ${answer}\nForce Exact Answer: ${entry.forceExactAnswer === true ? 'Yes' : 'No'}`,
        originalQuery: question,
        originalResponse: '',
        fineTunedAnswer: answer,
        forceExactAnswer: entry.forceExactAnswer === true,
        hiddenFromSources: true
      });
    }
    index = index + 1;
  }
  return files;
}

function resolveKnowledgeFileType(fileName) {
  const fileNameValue = typeof fileName === 'string' ? fileName : '';
  const parts = fileNameValue.split('.');
  const extension = parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
  if (extension === 'txt') {
    return 'text';
  }
  if (extension === 'pdf' || extension === 'doc' || extension === 'docx' || extension === 'xlsx') {
    return extension;
  }
  return '';
}

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const result = event !== undefined && event !== null && event.target !== undefined && event.target !== null ? event.target.result : '';
      const data = typeof result === 'string' && result.includes(',') === true ? result.split(',')[1] : '';
      if (data.length === 0) {
        reject(new Error('Could not read the selected document.'));
        return;
      }
      resolve(data);
    };
    reader.onerror = () => reject(new Error('Could not read the selected document.'));
    reader.readAsDataURL(file);
  });
}

async function buildKnowledgeFiles() {
  const sources = Array.isArray(wizardData.value.knowledgeSources) === true ? wizardData.value.knowledgeSources : [];
  const files = buildQuestionAnswerFiles();
  let sourceIndex = 0;
  while (sourceIndex < sources.length) {
    const source = sources[sourceIndex];
    if (source !== undefined && source !== null && source.type === 'website' && typeof source.detail === 'string' && source.detail.length > 0) {
      files.push({
        fileName: source.detail,
        fileType: 'website',
        status: 'new',
        url: source.detail,
        hiddenFromSources: false
      });
    }
    if (source !== undefined && source !== null && source.type === 'urls' && Array.isArray(source.urls) === true) {
      let urlIndex = 0;
      while (urlIndex < source.urls.length) {
        const url = source.urls[urlIndex];
        if (typeof url === 'string' && url.trim().length > 0) {
          files.push({
            fileName: url.trim(),
            fileType: 'websiteurl',
            status: 'new',
            url: url.trim(),
            hiddenFromSources: false
          });
        }
        urlIndex = urlIndex + 1;
      }
    }
    if (source !== undefined && source !== null && source.type === 'files' && Array.isArray(source.files) === true) {
      let fileIndex = 0;
      while (fileIndex < source.files.length) {
        const sourceFile = source.files[fileIndex];
        const fileName = sourceFile !== undefined && sourceFile !== null && typeof sourceFile.name === 'string' ? sourceFile.name : '';
        const fileType = resolveKnowledgeFileType(fileName);
        if (fileType.length === 0) {
          throw new Error(`Unsupported document type: ${fileName}`);
        }
        const fileData = await readFileAsBase64(sourceFile);
        files.push({
          fileName,
          fileType,
          fileData,
          content: fileType === 'text' ? await sourceFile.text() : null,
          status: 'new',
          hiddenFromSources: false
        });
        fileIndex = fileIndex + 1;
      }
    }
    sourceIndex = sourceIndex + 1;
  }
  return files;
}

function buildAgentDescription() {
  const descriptionParts = [];
  if (wizardData.value.typeLabel.length > 0) {
    descriptionParts.push(`Agent type: ${wizardData.value.typeLabel}`);
  }
  if (wizardData.value.jobDescription.length > 0) {
    descriptionParts.push(`Role: ${wizardData.value.jobDescription}`);
  }
  if (wizardData.value.personality.length > 0) {
    descriptionParts.push(`Personality: ${wizardData.value.personality}`);
  }
  if (wizardData.value.organizationName.length > 0) {
    descriptionParts.push(`Organization: ${wizardData.value.organizationName}`);
  }
  if (wizardData.value.organizationDescription.length > 0) {
    descriptionParts.push(`Organization context: ${wizardData.value.organizationDescription}`);
  }
  const hasVoiceChannel = wizardData.value.channels.includes('Phone') === true || wizardData.value.channels.includes('Web Voice Agent') === true || wizardData.value.channels.includes('Whatsapp Voice') === true;
  const hasChatChannel = wizardData.value.channels.includes('Web') === true || wizardData.value.channels.includes('Whatsapp') === true || wizardData.value.channels.includes('Email') === true;
  if (hasVoiceChannel === true && hasChatChannel === true) {
    descriptionParts.push('For chat, use clear structured answers. For voice, use short natural sentences, state essential details first, and avoid long lists unless asked.');
  } else if (hasVoiceChannel === true) {
    descriptionParts.push('This is a voice agent. Speak naturally using short sentences, confirm critical details, and avoid long lists unless the caller asks.');
  } else {
    descriptionParts.push('This is a chat agent. Use concise, clear answers with structure only when it improves comprehension.');
  }
  descriptionParts.push('Answer accurately using the configured knowledge base. If the answer is not available, say so clearly. Do not invent business policies, availability, pricing, or commitments.');
  return descriptionParts.join('\n\n');
}

function buildGeneratedRules() {
  const rules = [
    'Use the organization context and configured knowledge as the source of truth.',
    'When information is missing or uncertain, say so clearly and offer the next useful step.',
    'Do not make up policies, prices, availability, legal claims, or commitments.'
  ];
  if (wizardData.value.questionAnswers.some((entry) => entry.forceExactAnswer === true) === true) {
    rules.push('For question and answer entries marked as exact, provide the configured answer for materially similar questions.');
  }
  if (wizardData.value.channels.includes('Phone') === true || wizardData.value.channels.includes('Web Voice Agent') === true || wizardData.value.channels.includes('Whatsapp Voice') === true) {
    rules.push('For voice conversations, keep answers short and conversational, and confirm important details before acting.');
  }
  if (wizardData.value.selectedToolIds.length > 0) {
    rules.push('Use an integration only when it is needed for the user request and only after collecting the required information.');
  }
  return rules;
}

function buildStartingMessage() {
  const agentName = wizardData.value.agentName.trim().length > 0 ? wizardData.value.agentName.trim() : wizardData.value.organizationName.trim();
  if (agentName.length > 0) {
    return { default: `Hello, I am ${agentName}. How can I help you today?` };
  }
  return { default: 'Hello, how can I help you today?' };
}

async function createBaselineAgent() {
  const files = await buildKnowledgeFiles();
  const payload = {
    aiAgentName: wizardData.value.agentName.trim().length > 0 ? wizardData.value.agentName.trim() : wizardData.value.typeLabel,
    aiAgentStatus: 'active',
    agentType: 'PublicAgent',
    agentMode: 'Conversational',
    divisionIds: [],
    agentShortDescription: wizardData.value.jobDescription,
    agentData: {
      description: buildAgentDescription(),
      startingMessage: buildStartingMessage(),
      language: 'auto',
      setupType: 'guided',
      rules: buildGeneratedRules(),
      cachedFileNames: [],
      files,
      selectedToolIds: wizardData.value.selectedToolIds,
      configurations: {
        enableFeedbackButtons: true,
        enableSuggestedResponses: false,
        enableStreamingText: true,
        retrieveUserLocation: false,
        enableFileUploadInChat: false,
        clearSessionOnRefresh: false,
        deploymentChannels: wizardData.value.channels,
        toolSettings: wizardData.value.toolSettings,
        retrieverKValue: 8,
        vectorDbQueriesLimit: 1,
        maxOutputTokens: wizardData.value.channels.includes('Phone') === true || wizardData.value.channels.includes('Web Voice Agent') === true || wizardData.value.channels.includes('Whatsapp Voice') === true ? 700 : 1200
      },
      knowledgeBaseFolders: [],
      knowledgeBaseFileAssignments: {}
    }
  };
  const response = await axios.post(`${globalState.apiEndpoint}/createNewAIChat`, payload, { timeout: 0 });
  const responseData = response !== undefined && response !== null ? response.data : null;
  const rawAgentId = responseData !== null && responseData !== undefined && responseData.agentid !== undefined ? responseData.agentid : (responseData !== null && responseData !== undefined ? responseData.agentId : '');
  const agentId = rawAgentId !== undefined && rawAgentId !== null ? String(rawAgentId) : '';
  if (agentId.length === 0) {
    throw new Error('The agent was created without an agent ID.');
  }
  wizardData.value.agentId = agentId;
  try {
    const hasWhatsapp = wizardData.value.channels.includes('Whatsapp') === true || wizardData.value.channels.includes('Whatsapp Voice') === true;
    if (hasWhatsapp === true && wizardData.value.whatsappSourceId !== null) {
      await axios.post(`${globalState.apiEndpoint}/api/whatsapp/bind`, { agentId, sourceId: wizardData.value.whatsappSourceId });
    }
    if (wizardData.value.channels.includes('Phone') === true && wizardData.value.phoneSourceId !== null) {
      await axios.post(`${globalState.apiEndpoint}/api/phone/bind`, { agentId, sourceId: wizardData.value.phoneSourceId });
    }
  } catch (error) {
  }
}

function onGeneratingComplete() {
  showStepContent.value = false;
  currentStep.value = 'preview';
  questionKey.value = questionKey.value + 1;
  scrollToBottom();
}

function onAgentCreationError(message) {
  const errorMessage = typeof message === 'string' && message.length > 0 ? message : t('agentCreate.creationError.fallback', 'The agent could not be created. Please try again.');
  creationError.value = errorMessage;
}

function closeCreationError() {
  creationError.value = '';
  goToStep('platforms');
}

function restartWizard() {
  historyEntries.value = [];
  historyCounter = 0;
  userInput.value = '';
  selectedTypeId.value = '';
  selectedJobId.value = '';
  pendingKnowledgeSources.value = [];
  knowledgeSubStep.value = '';
  wizardData.value = {
    typeLabel: '',
    typeId: '',
    customType: '',
    jobDescription: '',
    agentName: '',
    personality: '',
    gender: '',
    knowledgeType: '',
    knowledgeLabel: '',
    knowledgeSources: [],
    questionAnswers: [],
    organizationName: '',
    organizationDescription: '',
    selectedToolIds: [],
    toolSettings: {},
    selectedTools: [],
    channels: [],
    whatsappSourceId: null,
    whatsappNumber: '',
    phoneSourceId: null,
    phoneNumber: '',
    agentId: ''
  };
  showStepContent.value = false;
  currentStep.value = 'type';
  questionKey.value = questionKey.value + 1;
}

onMounted(() => {
  if (hideSidebar !== undefined) {
    hideSidebar.value = false;
  }
  inputDirection.value = isRTL.value === true ? 'rtl' : 'ltr';
});
</script>

<style scoped>
.step-fade-enter-active {
  transition: opacity 420ms ease, transform 420ms ease;
}

.step-fade-enter-from {
  opacity: 0;
  transform: translateY(12px);
}

.step-fade-enter-to {
  opacity: 1;
  transform: translateY(0);
}
</style>
